import React from 'react';
import { Box, CircularProgress, Tab } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SETTINGS_CATEGORY_KEYS } from './constants';
import { CallLifecycleSettingsPanel } from './CallLifecycleSettingsPanel';
import { CallRoutingSettingsPanel } from './CallRoutingSettingsPanel';
import { AgentFilesSettingsPanel } from './AgentFilesSettingsPanel';
import { IdleAndCustomerSettingsPanel } from './IdleAndCustomerSettingsPanel';
import { IntegrationsSettingsPanel } from './IntegrationsSettingsPanel';
import { VoiceAnalysisSettingsPanel } from './VoiceAnalysisSettingsPanel';
import { WhatsAppSettingsPanel } from './WhatsAppSettingsPanel';
import {
  AgentAdvancedSettingsModalProps,
  ExpandedSettingsState,
  IdleStatePanelProps,
  SettingsCategoryKey,
  SetSettingsExpanded,
  TemplatePreviewValues,
} from './types';

type Props = Pick<
  AgentAdvancedSettingsModalProps,
  'agent' | 'agents' | 'campaignId' | 'campaignName' | 'companyName' | 'formMethods' | 'isReadOnly'
> & {
  activeCategory: SettingsCategoryKey;
  onCategoryChange: (event: React.SyntheticEvent, value: SettingsCategoryKey) => void;
  expandedSettings: ExpandedSettingsState;
  onExpandedChange: SetSettingsExpanded;
  idleState: Omit<IdleStatePanelProps, 'expanded' | 'onExpandedChange'>;
  allAgentTools: AgentAdvancedSettingsModalProps['campaignTools'];
  isLoadingAgentTools: boolean;
  refetchIntegrations: () => void;
};

const ToolSettingsLoading: React.FC = () => (
  <Box
    sx={{
      minHeight: 240,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
    <CircularProgress size={28} />
  </Box>
);

export const AgentAdvancedSettingsTabs: React.FC<Props> = ({
  activeCategory,
  onCategoryChange,
  agent,
  agents,
  campaignId,
  campaignName,
  companyName,
  formMethods,
  isReadOnly = false,
  expandedSettings,
  onExpandedChange,
  idleState,
  allAgentTools = [],
  isLoadingAgentTools,
  refetchIntegrations,
}) => {
  const { t } = useTranslation();
  const templatePreviewValues: TemplatePreviewValues = {
    agentName: agent.name || '',
    campaignName,
    companyName,
  };

  return (
    <TabContext value={activeCategory}>
      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Box
          sx={{
            width: { xs: 136, sm: 180 },
            flexShrink: 0,
            minHeight: 0,
            overflowY: 'auto',
            borderRight: 1,
            borderColor: 'divider',
          }}>
          <TabList
            onChange={onCategoryChange}
            orientation="vertical"
            variant="scrollable"
            visibleScrollbar
            aria-label={t('adminMainPage.agent.advancedSettingsTabs.label')}
            sx={{
              alignItems: 'stretch',
              '& .MuiTab-root': {
                alignItems: 'flex-start',
                minHeight: 40,
                px: 1.25,
                py: 1,
                fontSize: { xs: 11, sm: 12 },
                lineHeight: 1.25,
                textAlign: 'left',
                whiteSpace: 'normal',
              },
            }}>
            {SETTINGS_CATEGORY_KEYS.map((category) => (
              <Tab
                key={category}
                value={category}
                label={t(`adminMainPage.agent.advancedSettingsTabs.${category}`)}
              />
            ))}
          </TabList>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto' }}>
          <FormProvider {...formMethods}>
            <TabPanel value="callLifecycle" keepMounted sx={{ p: 0 }}>
              {isLoadingAgentTools ? (
                <ToolSettingsLoading />
              ) : (
                <CallLifecycleSettingsPanel isReadOnly={isReadOnly} />
              )}
            </TabPanel>
            <TabPanel value="idleAndCustomer" keepMounted sx={{ p: 0 }}>
              <IdleAndCustomerSettingsPanel
                {...idleState}
                expanded={expandedSettings}
                onExpandedChange={onExpandedChange}
              />
            </TabPanel>
            <TabPanel value="whatsapp" keepMounted sx={{ p: 0 }}>
              {isLoadingAgentTools ? (
                <ToolSettingsLoading />
              ) : (
                <WhatsAppSettingsPanel
                  isReadOnly={isReadOnly}
                  expanded={expandedSettings}
                  onExpandedChange={onExpandedChange}
                  templatePreviewValues={templatePreviewValues}
                />
              )}
            </TabPanel>
            <TabPanel value="callRouting" keepMounted sx={{ p: 0 }}>
              {isLoadingAgentTools ? (
                <ToolSettingsLoading />
              ) : (
                <CallRoutingSettingsPanel
                  isReadOnly={isReadOnly}
                  expanded={expandedSettings}
                  onExpandedChange={onExpandedChange}
                />
              )}
            </TabPanel>
            <TabPanel value="voiceAnalysis" keepMounted sx={{ p: 0 }}>
              {isLoadingAgentTools ? (
                <ToolSettingsLoading />
              ) : (
                <VoiceAnalysisSettingsPanel
                  isReadOnly={isReadOnly}
                  expanded={expandedSettings}
                  onExpandedChange={onExpandedChange}
                />
              )}
            </TabPanel>
            <TabPanel value="integrations" keepMounted sx={{ p: 0 }}>
              {isLoadingAgentTools ? (
                <ToolSettingsLoading />
              ) : (
                <IntegrationsSettingsPanel
                  agentId={agent.id || ''}
                  agents={agents}
                  tools={allAgentTools}
                  refetch={refetchIntegrations}
                  disabled={isReadOnly}
                />
              )}
            </TabPanel>
            <TabPanel value="files" keepMounted sx={{ p: 0 }}>
              <AgentFilesSettingsPanel campaignId={campaignId} agentId={agent.id || ''} />
            </TabPanel>
          </FormProvider>
        </Box>
      </Box>
    </TabContext>
  );
};
